import { QueryParamsType, ShopsQueryOptionsType, Shop } from '@framework/types';
import { CoreApi, ParamsType } from '@framework/utils/core-api';
import { mapPaginatorData } from '@framework/utils/data-mappers';
import { API_ENDPOINTS } from '@framework/utils/endpoints';
import {
  ShopNewsService,
  BestShopsService,
  ShopsService,
  ShopDetailService,
  FavoritesShopsService,
  ShopCommonService,
  ShopMenuService
} from './shops.service';
import {
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  useQuery,
} from 'react-query';
const ShopService = new CoreApi(API_ENDPOINTS.SHOPS);

type PaginatedShop = {
  data: Shop[];
  paginatorInfo: any;
};

type VoteWishlistsShopType = {
  shop_id: number | string;
};

type ReviewShopType = {
  shop_id: number | string;
  order_id: number | string;
  rate_price: number | string;
  rate_order: number | string;
  rate_return_goods: number | string;
};

const fetchShops = async ({
  queryKey,
  pageParam,
}: QueryParamsType): Promise<PaginatedShop> => {
  const params = queryKey[1];
  let fetchedData: any = {};
  if (pageParam) {
    const response = await ShopService.fetchUrl(pageParam);
    fetchedData = response.data;
  } else {
    const response = await ShopService.find(params as ParamsType);
    fetchedData = response.data;
  }
  const { data, ...rest } = fetchedData;
  return { data, paginatorInfo: mapPaginatorData({ ...rest }) };
};

const useShopsQuery = (
  params: ShopsQueryOptionsType = {},
  options?: UseInfiniteQueryOptions<
    PaginatedShop,
    Error,
    PaginatedShop,
    PaginatedShop,
    QueryKey
  >
) => {
  return useInfiniteQuery<PaginatedShop, Error>(
    [API_ENDPOINTS.SHOPS, params],
    fetchShops,
    {
      ...options,
      getNextPageParam: ({ paginatorInfo }) => paginatorInfo.nextPageUrl,
    }
  );
};

export const shopObjects = {
  bestShops: BestShopsService,
  newShops: ShopNewsService,
  shops: ShopsService,
  favoritesShops: FavoritesShopsService
}

const fetchPaginationShops = async (params: ShopsQueryOptionsType): Promise<{shops: Shop[]; paginatorInfo: any}> => {
  // @ts-ignore
  const response = await shopObjects[params.function]?.all(params);
  const { data, ...rest } = response || {};

  return { shops: data, paginatorInfo: mapPaginatorData({ ...rest })};
};

const useShopsPaginationQuery = (
  params: ShopsQueryOptionsType,
  options: any = {}
) => {
  return useQuery<any, Error>([API_ENDPOINTS.SHOPS, params], () => fetchPaginationShops(params), {
      ...options,
      keepPreviousData : true,
      staleTime: Infinity
    }
  );
};

export {
  useShopsQuery,
  fetchShops,
  fetchPaginationShops,
  useShopsPaginationQuery,
};

export const fetchShop = async (slug: string, locale: string = "vi") => {
  const data = await ShopDetailService.findBy(slug, locale);
  return data;
};

export const useShopQuery = (slug: string, locale: string) => {
  return useQuery<Shop, Error>([API_ENDPOINTS.SHOPS, slug], () =>
    fetchShop(slug, locale)
  );
};

export const fetchTopShop = async (limit: number | string, locale: string = "vi") => {
  const data = await ShopCommonService.fetchTopShop(limit, locale);

  return data;
};

export const useTopShopQuery = (limit: number | string, locale: string, options?: any) => {
  return useQuery<Shop, Error>([API_ENDPOINTS.SHOPS, {limit, locale}], () =>
    fetchTopShop(limit, locale), { ...options }
  );
};

export const useVoteWishlistsShopMutation = () => {
  return useMutation((input: VoteWishlistsShopType) =>
    ShopCommonService.voteWishlistsShop(input)
  );
};

export const fetchShopOrder = async (orderId: number | string, locale: string = "vi") => {
  const data = await ShopsService.all({orderId, locale, limit: 10000});

  return data;
};

export const useGetShopOrderQuery = (orderId: number | string, locale: string, options?: any) => {
  return useQuery<Shop, Error>([API_ENDPOINTS.SHOPS, {orderId, locale}], () =>
  fetchShopOrder(orderId, locale), { ...options }
  );
};

export const useReviewShopMutation = () => {
  return useMutation((input: ReviewShopType) =>
    ShopCommonService.reviewShop(input)
  );
};

export const fetchShopMenu = async (params: any = "vi") => {
  const data = await ShopMenuService.all(params);
  return data;
};

export const useShopMenuQuery = (params: any, options: any = {}) => {
  return useQuery<Shop, Error>([API_ENDPOINTS.SHOP_MENU, params], () =>
    fetchShopMenu(params),
    {...options}
  );
};
